"use client";

import { FaceLandmarker, FilesetResolver, type NormalizedLandmark } from "@mediapipe/tasks-vision";
import { useCallback, useEffect, useRef, useState } from "react";
import { logger } from "@/core/logging";
import { MEDIAPIPE_FACE_LANDMARKER_MODEL_URL, prefersMediapipeCpuDelegate, resolveVisionTasksFileset } from "./mediapipeConfig";
export type FaceStatus = "initializing" | "no-face" | "multiple-faces" | "too-close" | "too-far" | "look-left" | "look-right" | "look-up" | "look-down" | "tilt-head" | "perfect";
export interface FaceDetectionResult {
  status: FaceStatus;
  confidence: number;
  faceCount: number;
  landmarks: NormalizedLandmark[][] | null;
  yaw: number;
  pitch: number;
  roll: number;
  faceSize: number;
  centerX: number;
  centerY: number;
}

// Inter-pupil distance thresholds (normalized 0-1 relative to frame).
// IPD is far more stable than a full-landmark bounding box because it uses
// only two well-defined points and is unaffected by noisy edge landmarks,
// wide-angle lens distortion, or aspect-ratio differences.
export const IDEAL_FACE_SIZE_MIN = 0.045;
export const IDEAL_FACE_SIZE_MAX = 0.3;
export const YAW_THRESHOLD = 20;
export const PITCH_THRESHOLD = 24;
export const ROLL_THRESHOLD = 16;
const emptyDetectionResult = (status: FaceStatus): FaceDetectionResult => ({
  status,
  confidence: 0,
  faceCount: 0,
  landmarks: null,
  yaw: 0,
  pitch: 0,
  roll: 0,
  faceSize: 0,
  centerX: 0.5,
  centerY: 0.5
});

// MediaPipe's native procrustes_solver (needed only when facial transformation
// matrixes are requested) hits RET_CHECK failures on degenerate landmark
// configurations — partial faces, extreme angles, low-light frames. Those
// throws permanently corrupt the CalculatorGraph and every subsequent
// detectForVideo call on the same instance also throws. We never consume the
// transformation matrixes (yaw/pitch/roll are computed by hand from
// landmarks), so we keep this off.
const OUTPUT_FACIAL_TRANSFORMATION_MATRIXES = false;

// Live VIDEO detection stays at 1 face — bumping it costs every RAF frame and
// the status machine has no "multiple-faces" branch on the live path. Static
// IMAGE callers (e.g. upload validation) can opt into a higher bound for the
// duration of a single detect via `detectFaceInImage(url, { maxFaces })`.
const NUM_FACES = 1;
type Delegate = "GPU" | "CPU";
export type UseFaceLandmarkerOptions = {
  /**
   * Upper bound on normalized face size before guidance reports "too-close".
   * Defaults to {@link IDEAL_FACE_SIZE_MAX}. Flows that intentionally invite
   * a closer framing (e.g. lash/brow, where the eye region should fill more of
   * the frame) pass a larger value so a valid close shot is not rejected.
   */
  tooCloseFaceSizeMax?: number;
  /**
   * Gates the WASM + face_landmarker.task download (~15 MB from third-party
   * CDNs). While `false`, no network is started and the hook stays in
   * `isLoading`. Chrome's Heavy Ad Intervention unloads ad-tagged iframes
   * (e.g. embeds injected by A/B-testing scripts) that download more than
   * 4 MB before the user's first in-frame tap, so capture flows keep this
   * `false` during pre-interaction stages (consent/tips) and flip it on a
   * stage the user can only reach by tapping. Latched: once `true`, later
   * `false` values are ignored so the shared instance survives stage
   * transitions.
   */
  enabled?: boolean;
};
export function useFaceLandmarker(options?: UseFaceLandmarkerOptions) {
  const tooCloseFaceSizeMax = options?.tooCloseFaceSizeMax ?? IDEAL_FACE_SIZE_MAX;
  const enabled = options?.enabled ?? true;
  // Latch `enabled` on: once a capture flow flips it true (after the user's
  // in-frame tap), keep the shared MediaPipe instance alive across later stage
  // transitions even if a stage would momentarily report `enabled` false. The
  // latch lives in an effect rather than a render-phase set so it stays safe
  // under Strict/Concurrent rendering. `useState(enabled)` seeds it true for the
  // common always-enabled (non-embed) case, so there is no extra render there.
  const [activated, setActivated] = useState(enabled);
  useEffect(() => {
    if (enabled) {
      setActivated(true);
    }
  }, [enabled]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [runtimeError, setRuntimeError] = useState<string | null>(null);
  const lastResultRef = useRef<FaceDetectionResult | null>(null);
  const faceLandmarkerRef = useRef<FaceLandmarker | null>(null);
  const visionRef = useRef<Awaited<ReturnType<typeof FilesetResolver.forVisionTasks>> | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const lastVideoTimeRef = useRef<number>(-1);
  // We allow exactly one self-heal reinit per hook lifetime. A second failure
  // on the reinit'd instance is evidence the graph is fundamentally unhappy
  // on this device, and we commit to manual-capture mode instead of looping.
  const reinitAttemptedRef = useRef(false);
  const isReinitializingRef = useRef(false);
  const preferredDelegateRef = useRef<Delegate>("GPU");
  const haltVideoDetectionRef = useRef(false);
  // Shared mount flag so the async reinit path can skip state writes after
  // the capture flow is torn down (user navigates away mid-reinit, or
  // StrictMode re-mounts the hook in dev). Keeps React 18+ strict-mode clean
  // and avoids setRuntimeError-after-unmount warnings.
  const isMountedRef = useRef(true);
  function createLandmarker(vision: Awaited<ReturnType<typeof FilesetResolver.forVisionTasks>>, delegate: Delegate) {
    return FaceLandmarker.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath: MEDIAPIPE_FACE_LANDMARKER_MODEL_URL,
        delegate
      },
      runningMode: "VIDEO",
      numFaces: NUM_FACES,
      minFaceDetectionConfidence: 0.5,
      minFacePresenceConfidence: 0.5,
      minTrackingConfidence: 0.5,
      outputFaceBlendshapes: false,
      outputFacialTransformationMatrixes: OUTPUT_FACIAL_TRANSFORMATION_MATRIXES
    });
  }
  useEffect(() => {
    if (!activated) {
      return;
    }
    isMountedRef.current = true;
    async function init() {
      try {
        setIsLoading(true);
        setError(null);
        const vision_0 = await resolveVisionTasksFileset();
        visionRef.current = vision_0;
        let faceLandmarker: FaceLandmarker;
        if (prefersMediapipeCpuDelegate()) {
          faceLandmarker = await createLandmarker(vision_0, "CPU");
          preferredDelegateRef.current = "CPU";
        } else {
          try {
            faceLandmarker = await createLandmarker(vision_0, "GPU");
            preferredDelegateRef.current = "GPU";
          } catch (gpuErr) {
            logger.warn("face_landmarker_gpu_failed_falling_back_to_cpu", {
              error: gpuErr instanceof Error ? gpuErr.message : String(gpuErr)
            });
            faceLandmarker = await createLandmarker(vision_0, "CPU");
            preferredDelegateRef.current = "CPU";
          }
        }
        if (isMountedRef.current) {
          faceLandmarkerRef.current = faceLandmarker;
          haltVideoDetectionRef.current = false;
          setIsInitialized(true);
          setIsLoading(false);
        } else {
          try {
            faceLandmarker.close();
          } catch {}
        }
      } catch (err) {
        logger.error("failed_to_initialize_face_landmarker", undefined, err as Error);
        if (isMountedRef.current) {
          setError("Failed to load face detection model");
          setIsLoading(false);
        }
      }
    }
    void init();
    return () => {
      isMountedRef.current = false;
      if (faceLandmarkerRef.current) {
        try {
          faceLandmarkerRef.current.close();
        } catch {}
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
    // `activated` only ever transitions false -> true, so this effect runs its
    // init exactly once, on activation.
  }, [activated]);
  const attemptReinit = useCallback(async (context: string): Promise<boolean> => {
    if (reinitAttemptedRef.current) return false;
    if (isReinitializingRef.current) return false;
    const vision_1 = visionRef.current;
    if (!vision_1) return false;
    reinitAttemptedRef.current = true;
    isReinitializingRef.current = true;
    logger.warn("face_landmarker_reinit_started", {
      context
    });
    try {
      faceLandmarkerRef.current?.close();
    } catch {}
    faceLandmarkerRef.current = null;

    // After a native graph RET_CHECK, the GPU-backed graph is often the one
    // in a bad state on flaky WebGL contexts. Always reinit on CPU — it's
    // slower per frame but dramatically more stable, and at this point we
    // are racing against permanent fallback to manual capture.
    const REINIT_DELEGATE: Delegate = "CPU";
    try {
      const landmarker = await createLandmarker(vision_1, REINIT_DELEGATE);
      if (!isMountedRef.current) {
        try {
          landmarker.close();
        } catch {}
        return false;
      }
      faceLandmarkerRef.current = landmarker;
      preferredDelegateRef.current = REINIT_DELEGATE;
      logger.warn("face_landmarker_reinit_succeeded", {
        context,
        delegate: REINIT_DELEGATE
      });
      return true;
    } catch (err_0) {
      logger.error("face_landmarker_reinit_failed", {
        context
      }, err_0 as Error);
      return false;
    } finally {
      isReinitializingRef.current = false;
    }
  }, []);
  const handleDetectionFailure = useCallback((err_1: unknown, context_0: string) => {
    const message = err_1 instanceof Error ? err_1.message : String(err_1);
    // Identify the procrustes/calculator-graph family distinctly so we can
    // measure baseline/fix-rate in RUM rather than grouping under generic
    // JS errors.
    const isWasmNativeFailure = /CalculatorGraph|procrustes|RET_CHECK|_malloc|Out of bounds memory access|GLctx|activeTexture|_emscripten_gl|WebGL/i.test(message);
    if (haltVideoDetectionRef.current) {
      return;
    }
    logger.error("face_landmarker_detect_failed", {
      context: context_0,
      message,
      errorFamily: isWasmNativeFailure ? "wasm_native" : "unknown",
      reinitAttempted: reinitAttemptedRef.current
    }, err_1 instanceof Error ? err_1 : new Error(message));
    void (async () => {
      const recovered = await attemptReinit(context_0);
      if (!isMountedRef.current) return;
      if (!recovered) {
        haltVideoDetectionRef.current = true;
        setRuntimeError(message);
      }
    })();
  }, [attemptReinit]);
  const calculateFaceOrientation = useCallback((landmarks: NormalizedLandmark[]) => {
    const noseTip = landmarks[1];
    const leftEye = landmarks[33];
    const rightEye = landmarks[263];
    const forehead = landmarks[10];
    const chin = landmarks[152];
    const eyeMidX = (leftEye.x + rightEye.x) / 2;
    const eyeDistance = Math.abs(rightEye.x - leftEye.x);
    const noseOffset = (noseTip.x - eyeMidX) / eyeDistance;
    const yaw = noseOffset * 60;
    const eyeMidY = (leftEye.y + rightEye.y) / 2;
    const verticalFaceSpan = chin.y - forehead.y;
    const expectedNoseY = eyeMidY + verticalFaceSpan * 0.2;
    const noseYOffset = noseTip.y - expectedNoseY;
    const pitch = noseYOffset / verticalFaceSpan * 80;
    const eyeSlope = (rightEye.y - leftEye.y) / (rightEye.x - leftEye.x);
    const roll = Math.atan(eyeSlope) * (180 / Math.PI);
    const faceSize = Math.sqrt(Math.pow(rightEye.x - leftEye.x, 2) + Math.pow(rightEye.y - leftEye.y, 2));
    const centerX = eyeMidX;
    const centerY = (forehead.y + chin.y) / 2;
    return {
      yaw,
      pitch,
      roll,
      faceSize,
      centerX,
      centerY
    };
  }, []);
  const determineFaceStatus = useCallback((faceCount: number, yaw_0: number, pitch_0: number, roll_0: number, faceSize_0: number): FaceStatus => {
    if (faceCount === 0) return "no-face";
    if (faceSize_0 < IDEAL_FACE_SIZE_MIN) return "too-far";
    if (faceSize_0 > tooCloseFaceSizeMax) return "too-close";
    if (Math.abs(roll_0) > ROLL_THRESHOLD) return "tilt-head";
    if (yaw_0 < -YAW_THRESHOLD) return "look-right";
    if (yaw_0 > YAW_THRESHOLD) return "look-left";
    if (pitch_0 > PITCH_THRESHOLD) return "look-up";
    if (pitch_0 < -PITCH_THRESHOLD) return "look-down";
    return "perfect";
  }, [tooCloseFaceSizeMax]);
  const detectFace = useCallback((video: HTMLVideoElement): FaceDetectionResult | null => {
    if (haltVideoDetectionRef.current) {
      return null;
    }
    // Order matters: the reinit path briefly nulls faceLandmarkerRef.current
    // to release the broken graph before creating the replacement. Check
    // the reinit flag FIRST so the "hold last result" path is reachable
    // during that 50-200ms window. Only fall through to the null-ref guard
    // when we've never initialized at all (or reinit has failed and we're
    // now in runtimeError mode).
    if (isReinitializingRef.current) return lastResultRef.current;
    if (!faceLandmarkerRef.current || !isInitialized) return null;
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      return lastResultRef.current;
    }
    if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
      return lastResultRef.current;
    }
    if (video.currentTime === lastVideoTimeRef.current) {
      return lastResultRef.current;
    }
    lastVideoTimeRef.current = video.currentTime;
    const startTime = performance.now();
    let results;
    try {
      results = faceLandmarkerRef.current.detectForVideo(video, startTime);
    } catch (err_2) {
      handleDetectionFailure(err_2, "detectForVideo");
      return lastResultRef.current;
    }
    const faceCount_0 = results.faceLandmarks.length;
    if (faceCount_0 === 0) {
      const result = emptyDetectionResult("no-face");
      lastResultRef.current = result;
      return result;
    }
    const landmarks_0 = results.faceLandmarks[0];
    const {
      yaw: yaw_1,
      pitch: pitch_1,
      roll: roll_1,
      faceSize: faceSize_1,
      centerX: centerX_0,
      centerY: centerY_0
    } = calculateFaceOrientation(landmarks_0);
    const status = determineFaceStatus(faceCount_0, yaw_1, pitch_1, roll_1, faceSize_1);
    const result_0: FaceDetectionResult = {
      status,
      confidence: 0.9,
      faceCount: faceCount_0,
      landmarks: results.faceLandmarks,
      yaw: yaw_1,
      pitch: pitch_1,
      roll: roll_1,
      faceSize: faceSize_1,
      centerX: centerX_0,
      centerY: centerY_0
    };
    lastResultRef.current = result_0;
    return result_0;
  }, [isInitialized, calculateFaceOrientation, determineFaceStatus, handleDetectionFailure]);
  const detectFaceInImage = useCallback((imageDataUrl: string, options_0?: {
    maxFaces?: number;
  }): Promise<FaceDetectionResult> => {
    // Per-call override of the configured `numFaces`. Used by upload
    // validation to surface `faceCount > 1` from a single still image
    // without raising the cost of the live VIDEO RAF loop.
    const maxFaces = options_0?.maxFaces ?? NUM_FACES;
    return new Promise(resolve => {
      if (haltVideoDetectionRef.current) {
        resolve(emptyDetectionResult("no-face"));
        return;
      }
      if (!faceLandmarkerRef.current || !isInitialized) {
        resolve(emptyDetectionResult("initializing"));
        return;
      }
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = async () => {
        if (haltVideoDetectionRef.current) {
          resolve(emptyDetectionResult("no-face"));
          return;
        }
        try {
          const faceLandmarker_0 = faceLandmarkerRef.current;
          if (!faceLandmarker_0) {
            resolve(emptyDetectionResult("no-face"));
            return;
          }
          await faceLandmarker_0.setOptions({
            runningMode: "IMAGE",
            numFaces: maxFaces
          });
          const results_0 = faceLandmarker_0.detect(img);
          await faceLandmarker_0.setOptions({
            runningMode: "VIDEO",
            numFaces: NUM_FACES
          });
          const faceCount_1 = results_0.faceLandmarks.length;
          if (faceCount_1 === 0) {
            resolve(emptyDetectionResult("no-face"));
            return;
          }
          const landmarks_1 = results_0.faceLandmarks[0];
          const {
            yaw: yaw_2,
            pitch: pitch_2,
            roll: roll_2,
            faceSize: faceSize_2,
            centerX: centerX_1,
            centerY: centerY_1
          } = calculateFaceOrientation(landmarks_1);
          const status_0 = determineFaceStatus(faceCount_1, yaw_2, pitch_2, roll_2, faceSize_2);
          resolve({
            status: status_0,
            confidence: 0.9,
            faceCount: faceCount_1,
            landmarks: results_0.faceLandmarks,
            yaw: yaw_2,
            pitch: pitch_2,
            roll: roll_2,
            faceSize: faceSize_2,
            centerX: centerX_1,
            centerY: centerY_1
          });
        } catch (err_3) {
          handleDetectionFailure(err_3, "detectImage");
          try {
            await faceLandmarkerRef.current?.setOptions({
              runningMode: "VIDEO",
              numFaces: NUM_FACES
            });
          } catch {}
          resolve(emptyDetectionResult("no-face"));
        }
      };
      img.onerror = () => {
        logger.error("face_landmarker_image_load_failed");
        resolve(emptyDetectionResult("no-face"));
      };
      img.src = imageDataUrl;
    });
  }, [isInitialized, calculateFaceOrientation, determineFaceStatus, handleDetectionFailure]);
  const startDetection = useCallback((video_0: HTMLVideoElement, onResult: (result: FaceDetectionResult) => void) => {
    // detectFace is already crash-safe internally, but wrap the callback
    // so a consumer-side throw also cannot kill the RAF loop.
    const runDetectionLoop = () => {
      if (!video_0.paused && !video_0.ended) {
        const result_1 = detectFace(video_0);
        if (result_1) {
          try {
            onResult(result_1);
          } catch (err_4) {
            logger.error("face_landmarker_on_result_handler_threw", undefined, err_4 instanceof Error ? err_4 : new Error(String(err_4)));
          }
        }
      }
      animationFrameRef.current = requestAnimationFrame(runDetectionLoop);
    };
    runDetectionLoop();
  }, [detectFace]);
  const stopDetection = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    lastVideoTimeRef.current = -1;
  }, []);
  return {
    isInitialized,
    isLoading,
    error,
    runtimeError,
    detectFace,
    detectFaceInImage,
    startDetection,
    stopDetection
  };
}
export function getGuidanceMessage(status: FaceStatus): {
  text: string;
  subtext?: string;
} {
  switch (status) {
    case "initializing":
      return {
        text: "Preparing camera...",
        subtext: "Loading face detection"
      };
    case "no-face":
      return {
        text: "Position your face in the frame",
        subtext: "Look straight at the camera"
      };
    case "multiple-faces":
      return {
        text: "We see more than one face",
        subtext: "We'll focus on the primary face"
      };
    case "too-close":
      return {
        text: "Move back a bit",
        subtext: "You're too close to the camera"
      };
    case "too-far":
      return {
        text: "Come closer",
        subtext: "Move toward the camera"
      };
    case "look-left":
      return {
        text: "Turn slightly left",
        subtext: "Face the camera directly"
      };
    case "look-right":
      return {
        text: "Turn slightly right",
        subtext: "Face the camera directly"
      };
    case "look-up":
      return {
        text: "Lower your chin",
        subtext: "Look straight ahead"
      };
    case "look-down":
      return {
        text: "Raise your chin",
        subtext: "Look straight ahead"
      };
    case "tilt-head":
      return {
        text: "Level your head",
        subtext: "Keep your head straight"
      };
    case "perfect":
      return {
        text: "Perfect! Hold still...",
        subtext: "Getting ready to capture"
      };
  }
}